
import React, {
    useEffect,
    useMemo,
    useState,
} from 'react';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import {
    ADMIN_PRODUCT_3D_LIGHTING_PRESETS,
    getDefaultAdminProduct3DLightingPatch,
    normalizeAdminProduct3DSceneSettings,
} from './adminProduct3DSceneSettings';

const VIEW_MODES = [
    {
        value: 'light',
        label: 'Светлый',
        description: 'Светлый фон и нейтральный пол.',
        icon: WbSunnyOutlinedIcon,
    },
    {
        value: 'studio',
        label: 'Студия',
        description: 'Тёмная студия с контрастной сеткой.',
        icon: AutoAwesomeOutlinedIcon,
    },
    {
        value: 'dark',
        label: 'Тёмный',
        description: 'Глубокий тёмный фон для светлого камня.',
        icon: DarkModeOutlinedIcon,
    },
];

const formatValue = (value, digits = 2) => (
    Number(value || 0).toFixed(digits)
);

const ToolIcon = ({ type }) => {
    if (type === 'background') {
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 5h16v14H4z" />
                <path d="m5 16 4.2-4.4 3.1 3 2.2-2.2L19 17" />
                <circle cx="15.5" cy="8.5" r="1.5" />
            </svg>
        );
    }

    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="3.3" />
            <path d="M12 2v2.2M12 19.8V22M2 12h2.2M19.8 12H22M4.9 4.9l1.6 1.6M17.5 17.5l1.6 1.6M19.1 4.9l-1.6 1.6M6.5 17.5l-1.6 1.6" />
        </svg>
    );
};

const SceneRange = ({
    label,
    value,
    min,
    max,
    step,
    suffix = '',
    digits = 2,
    syncKey = '',
    onChange,
}) => (
    <label className="adminProduct3DSceneTools__range">
        <span>
            <b>{label}</b>
            <strong>
                {formatValue(value, digits)}
                {suffix}
            </strong>
        </span>

        <input
            type="range"
            data-catalog-sync-key={syncKey || undefined}
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={(event) => onChange(
                Number(event.target.value)
            )}
        />
    </label>
);

const SceneSwitch = ({
    title,
    text,
    checked,
    syncKey = '',
    onChange,
}) => (
    <label className="adminProduct3DSceneTools__switch">
        <span>
            <b>{title}</b>
            <small>{text}</small>
        </span>

        <input
            type="checkbox"
            data-catalog-sync-key={syncKey || undefined}
            checked={!!checked}
            onChange={(event) => onChange(event.target.checked)}
        />
    </label>
);

const AdminProduct3DSceneTools = ({
    stageKey = 'product-3d',
    viewMode = 'light',
    onViewModeChange,
    sceneSettings = null,
    onSceneSettingChange,
    onSceneSettingsChange,
}) => {
    const settings = useMemo(
        () => normalizeAdminProduct3DSceneSettings(sceneSettings),
        [sceneSettings]
    );
    const [openPanels, setOpenPanels] = useState({
        background: false,
        lighting: false,
    });
    const [panoramaDraft, setPanoramaDraft] = useState(
        settings.backgroundUrl
    );

    useEffect(() => {
        setPanoramaDraft(settings.backgroundUrl);
    }, [settings.backgroundUrl]);

    useEffect(() => {
        setOpenPanels({
            background: false,
            lighting: false,
        });
    }, [stageKey]);

    const patchSettings = (patch) => {
        if (typeof onSceneSettingsChange === 'function') {
            onSceneSettingsChange(patch);
            return;
        }

        Object.entries(patch).forEach(([key, value]) => {
            onSceneSettingChange?.(key, value);
        });
    };

    const togglePanel = (panel) => {
        setOpenPanels((previous) => ({
            ...previous,
            [panel]: !previous[panel],
        }));
    };

    const closePanel = (panel) => {
        setOpenPanels((previous) => ({
            ...previous,
            [panel]: false,
        }));
    };

    const selectTheme = (mode) => {
        onViewModeChange?.(mode);
        patchSettings({
            backgroundMode: 'theme',
        });
    };

    const applyPanorama = () => {
        const url = String(panoramaDraft || '').trim();

        if (!url) return;

        patchSettings({
            backgroundMode: 'panorama',
            backgroundUrl: url,
        });
    };

    const clearPanorama = () => {
        setPanoramaDraft('');
        patchSettings({
            backgroundMode: 'theme',
            backgroundUrl: '',
        });
    };

    return (
        <div
            className="adminProduct3DSceneTools"
            onPointerDown={(event) => event.stopPropagation()}
            onWheel={(event) => event.stopPropagation()}
        >
            <div
                className="adminProduct3DSceneTools__buttons"
                aria-label="Инструменты оформления 3D-сцены"
            >
                <button
                    type="button"
                    className={[
                        'adminProduct3DSceneTools__toggle',
                        openPanels.background ? 'is-active' : '',
                        settings.backgroundMode === 'panorama'
                            ? 'has-custom-value'
                            : '',
                    ].filter(Boolean).join(' ')}
                    aria-expanded={openPanels.background}
                    aria-controls={`scene-background-${stageKey}`}
                    title="Фон и окружение сцены"
                    data-catalog-sync-key={`scene-background-toggle-${stageKey}`}
                    onClick={() => togglePanel('background')}
                >
                    <ToolIcon type="background" />
                    <span>Фон</span>
                </button>

                <button
                    type="button"
                    className={[
                        'adminProduct3DSceneTools__toggle',
                        openPanels.lighting ? 'is-active' : '',
                    ].filter(Boolean).join(' ')}
                    aria-expanded={openPanels.lighting}
                    aria-controls={`scene-lighting-${stageKey}`}
                    title="Свет и тени"
                    data-catalog-sync-key={`scene-lighting-toggle-${stageKey}`}
                    onClick={() => togglePanel('lighting')}
                >
                    <ToolIcon type="lighting" />
                    <span>Свет</span>
                </button>
            </div>

            <div className="adminProduct3DSceneTools__panels">
                {openPanels.background ? (
                    <section
                        id={`scene-background-${stageKey}`}
                        className="adminProduct3DSceneTools__panel"
                    >
                        <header>
                            <div>
                                <strong>Фон сцены</strong>
                                <span>
                                    Обычный фон или панорама 360°.
                                </span>
                            </div>

                            <button
                                type="button"
                                aria-label="Свернуть фон сцены"
                                data-catalog-sync-key={`scene-background-close-${stageKey}`}
                                onClick={() => closePanel('background')}
                            >
                                ×
                            </button>
                        </header>

                        <div className="adminProduct3DSceneTools__backgrounds">
                            {VIEW_MODES.map((mode) => {
                                const Icon = mode.icon;
                                const active = (
                                    settings.backgroundMode === 'theme'
                                    && viewMode === mode.value
                                );

                                return (
                                    <button
                                        type="button"
                                        key={mode.value}
                                        data-catalog-sync-key={`scene-theme-${stageKey}-${mode.value}`}
                                        className={active ? 'is-active' : ''}
                                        aria-pressed={active}
                                        onClick={() => selectTheme(mode.value)}
                                    >
                                        <Icon fontSize="small" />
                                        <span>
                                            <b>{mode.label}</b>
                                            <small>{mode.description}</small>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        <details className="adminProduct3DSceneTools__advanced">
                            <summary>Панорама 360° и окружение</summary>

                            <div className="adminProduct3DSceneTools__panorama">
                                <div>
                                    <strong>Панорама 360°</strong>
                                    <small>
                                        JPG/PNG 2:1, желательно не больше 4096×2048. Например:
                                        /scene-backgrounds/forest-360.jpg.
                                    </small>
                                </div>

                                <input
                                    type="text"
                                    data-catalog-sync-key={`scene-panorama-url-${stageKey}`}
                                    value={panoramaDraft}
                                    placeholder="/scene-backgrounds/forest-360.jpg"
                                    onChange={(event) => setPanoramaDraft(
                                        event.target.value
                                    )}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            applyPanorama();
                                        }
                                    }}
                                />

                                <div>
                                    <button
                                        type="button"
                                        className="is-primary"
                                        data-catalog-sync-key={`scene-panorama-apply-${stageKey}`}
                                        disabled={!String(panoramaDraft || '').trim()}
                                        onClick={applyPanorama}
                                    >
                                        Применить
                                    </button>

                                    <button
                                        type="button"
                                        data-catalog-sync-key={`scene-panorama-clear-${stageKey}`}
                                        disabled={(
                                            !settings.backgroundUrl
                                            && !panoramaDraft
                                        )}
                                        onClick={clearPanorama}
                                    >
                                        Очистить
                                    </button>
                                </div>
                            </div>

                            <SceneSwitch
                                syncKey={`scene-background-environment-${stageKey}`}
                                title="Окружение из панорамы"
                                text="Панорама влияет на блики гранита."
                                checked={settings.backgroundAsEnvironment}
                                onChange={(value) => patchSettings({
                                    backgroundAsEnvironment: value,
                                })}
                            />

                            <SceneSwitch
                                syncKey={`scene-ground-${stageKey}`}
                                title="Пол и сетка"
                                text="Скрыть для чистого 360°-окружения."
                                checked={settings.showGround}
                                onChange={(value) => patchSettings({
                                    showGround: value,
                                })}
                            />
                        </details>
                    </section>
                ) : null}

                {openPanels.lighting ? (
                    <section
                        id={`scene-lighting-${stageKey}`}
                        className="adminProduct3DSceneTools__panel"
                    >
                        <header>
                            <div>
                                <strong>Свет и тени</strong>
                                <span>
                                    Только основной свет создаёт тень.
                                </span>
                            </div>

                            <button
                                type="button"
                                aria-label="Свернуть свет"
                                data-catalog-sync-key={`scene-lighting-close-${stageKey}`}
                                onClick={() => closePanel('lighting')}
                            >
                                ×
                            </button>
                        </header>

                        <div className="adminProduct3DSceneTools__presets">
                            {ADMIN_PRODUCT_3D_LIGHTING_PRESETS.map(
                                (preset) => (
                                    <button
                                        type="button"
                                        key={preset.value}
                                        data-catalog-sync-key={`scene-lighting-preset-${stageKey}-${preset.value}`}
                                        title={preset.description}
                                        onClick={() => patchSettings(
                                            preset.settings
                                        )}
                                    >
                                        {preset.label}
                                    </button>
                                )
                            )}
                        </div>

                        <SceneRange
                            syncKey={`scene-sun-intensity-${stageKey}`}
                            label="Основной свет"
                            value={settings.sunIntensity}
                            min="0"
                            max="10"
                            step="0.05"
                            onChange={(value) => patchSettings({
                                sunIntensity: value,
                            })}
                        />

                        <SceneRange
                            syncKey={`scene-fill-intensity-${stageKey}`}
                            label="Свет с обратной стороны"
                            value={settings.fillIntensity}
                            min="0"
                            max="3"
                            step="0.05"
                            onChange={(value) => patchSettings({
                                fillIntensity: value,
                            })}
                        />


                        <details className="adminProduct3DSceneTools__advanced">
                            <summary>Направление и точная настройка</summary>

                            <SceneRange
                                syncKey={`scene-rim-intensity-${stageKey}`}
                                label="Контровой свет"
                                value={settings.rimIntensity}
                                min="0"
                                max="3"
                                step="0.05"
                                onChange={(value) => patchSettings({
                                    rimIntensity: value,
                                })}
                            />

                            <SceneRange
                                syncKey={`scene-sun-angle-${stageKey}`}
                                label="Азимут солнца"
                                value={settings.sunAngle}
                                min="0"
                                max="360"
                                step="1"
                                suffix="°"
                                digits={0}
                                onChange={(value) => patchSettings({
                                    sunAngle: value,
                                })}
                            />

                            <SceneRange
                                syncKey={`scene-sun-vertical-${stageKey}`}
                                label="Высота солнца"
                                value={settings.sunVertical}
                                min="-20"
                                max="89"
                                step="1"
                                suffix="°"
                                digits={0}
                                onChange={(value) => patchSettings({
                                    sunVertical: value,
                                })}
                            />

                            <SceneRange
                                syncKey={`scene-hemisphere-intensity-${stageKey}`}
                                label="Окружение"
                                value={settings.hemisphereIntensity}
                                min="0"
                                max="2"
                                step="0.01"
                                onChange={(value) => patchSettings({
                                    hemisphereIntensity: value,
                                })}
                            />

                            <SceneRange
                                syncKey={`scene-ambient-intensity-${stageKey}`}
                                label="Мягкий общий свет"
                                value={settings.ambientIntensity}
                                min="0"
                                max="1"
                                step="0.01"
                                onChange={(value) => patchSettings({
                                    ambientIntensity: value,
                                })}
                            />

                            <SceneSwitch
                                syncKey={`scene-shadows-${stageKey}`}
                                title="Тени на сцене"
                                text="Тень создаёт только основной свет."
                                checked={settings.shadowsEnabled}
                                onChange={(value) => patchSettings({
                                    shadowsEnabled: value,
                                })}
                            />
                        </details>

                        <button
                            type="button"
                            className="adminProduct3DSceneTools__reset"
                            data-catalog-sync-key={`scene-lighting-reset-${stageKey}`}
                            onClick={() => patchSettings(
                                getDefaultAdminProduct3DLightingPatch()
                            )}
                        >
                            <RestartAltIcon fontSize="small" />
                            По умолчанию
                        </button>
                    </section>
                ) : null}
            </div>
        </div>
    );
};

export default AdminProduct3DSceneTools;
